import type { ProjectState } from "hooks/useProjects/useProjects.types";

const SCHEDULING_RERUN = "scheduling_rerun";
const BUDGET_RERUN = "budget_rerun";

export type RerunAvailability = {
  exhausted: boolean;
  remainingUnits: number;
  cost: number;
  /** The plan covers the run, so no credits are taken. */
  unlimited?: boolean;
};

/**
 * One line about what a budget rerun costs, matching the full rerun dialog
 * (EditBudgetAssumptionsModal): its upgrade text on a free plan, nothing on plans that include
 * AI, its no-credits text when none are left, else "Costs 5 credits." ("Costs 1 credit." on
 * legacy rerun-count projects). Null means show no cost line.
 */
export function budgetRerunCostLine(
  availability: RerunAvailability,
  plan: { isFreePlan?: boolean } = {}
): string | null {
  const { cost } = availability;
  if (plan.isFreePlan) return "Upgrade to access this feature.";
  if (availability.unlimited) return null;
  if (availability.exhausted) return `No AI credits left for rebudget (${cost} credits per run).`;
  return `Costs ${cost} ${cost === 1 ? "credit" : "credits"}.`;
}

/**
 * When the Run AI dialog shows "Unlimited" instead of a balance: the server
 * flag, or a workspace subscription wallet. The header's Run AI count uses
 * the same rule, so the two never disagree.
 */
export function isRunAiCreditsUnlimited(
  aiCredits:
    | Pick<NonNullable<ProjectState["aiCredits"]>, "unlimited" | "walletKind">
    | null
    | undefined
): boolean {
  return aiCredits?.unlimited === true || aiCredits?.walletKind === "workspace_subscription";
}

function isSubscriptionAiUnlimited(project: ProjectState | null | undefined): boolean {
  const ac = project?.aiCredits;
  if (!ac) return false;
  // The server decides this; it already knows whether the subscription is entitled. Only an
  // absent flag falls back. Treating anything other than `true` as "keep guessing" meant an
  // explicit `false` for a lapsed subscriber was overridden here, so the UI offered reruns the
  // backend then refused.
  if (typeof ac.unlimited === "boolean") return ac.unlimited;
  // Legacy payloads predating the flag: a workspace wallet implied paid SaaS.
  return ac.walletKind === "workspace_subscription";
}

function getCostForKind(kind: "schedule" | "budget", project: ProjectState | null | undefined) {
  const key = kind === "schedule" ? SCHEDULING_RERUN : BUDGET_RERUN;
  const fromApi = project?.aiCredits?.actionCosts?.[key];
  if (typeof fromApi === "number" && Number.isFinite(fromApi)) {
    return Math.max(1, Math.floor(fromApi));
  }
  return 5;
}

export function getScheduleRerunAvailability(
  project: ProjectState | null | undefined
): RerunAvailability {
  const cost = getCostForKind("schedule", project);
  const ac = project?.aiCredits;
  if (ac != null && typeof ac.remaining === "number") {
    if (isSubscriptionAiUnlimited(project)) {
      return {
        exhausted: false,
        remainingUnits: ac.remaining,
        cost,
      };
    }
    return {
      exhausted: ac.remaining < cost,
      remainingUnits: ac.remaining,
      cost,
    };
  }
  const legacyLeft = project?.scheduleRerunsLeft ?? 0;
  return {
    exhausted: legacyLeft < 1,
    remainingUnits: legacyLeft,
    cost: 1,
  };
}

export function getBudgetRerunAvailability(
  project: ProjectState | null | undefined
): RerunAvailability {
  const cost = getCostForKind("budget", project);
  const ac = project?.aiCredits;
  if (ac != null && typeof ac.remaining === "number") {
    if (isSubscriptionAiUnlimited(project)) {
      return {
        exhausted: false,
        remainingUnits: ac.remaining,
        cost,
        unlimited: true,
      };
    }
    return {
      exhausted: ac.remaining < cost,
      remainingUnits: ac.remaining,
      cost,
    };
  }
  const legacyLeft = project?.budgetRerunsLeft ?? 0;
  return {
    exhausted: legacyLeft < 1,
    remainingUnits: legacyLeft,
    cost: 1,
  };
}

/** AI credit pool rerun (script breakdown, character breakdown, coverage, etc.). */
export function getAiCreditsActionRerunAvailability(
  project: ProjectState | null | undefined,
  actionKey: string
): RerunAvailability {
  const fromApi = project?.aiCredits?.actionCosts?.[actionKey];
  const cost =
    typeof fromApi === "number" && Number.isFinite(fromApi) ? Math.max(1, Math.floor(fromApi)) : 5;
  const ac = project?.aiCredits;
  if (ac != null && typeof ac.remaining === "number") {
    if (isSubscriptionAiUnlimited(project)) {
      return {
        exhausted: false,
        remainingUnits: ac.remaining,
        cost,
      };
    }
    return {
      exhausted: ac.remaining < cost,
      remainingUnits: ac.remaining,
      cost,
    };
  }
  return {
    exhausted: true,
    remainingUnits: 0,
    cost,
  };
}
